import { useState } from 'react';
import toast from 'react-hot-toast';
import {
    Box, Typography, Button, IconButton,
    TextField, Chip, Divider,
    Table, TableHead, TableBody, TableRow, TableCell, TableContainer,
    Dialog, DialogTitle, DialogContent, DialogActions,
} from '@mui/material';
import { Add, Delete, Close, RestartAlt } from '@mui/icons-material';
import FlexBetween from 'components/FlexBetween';
import { DEFAULT_PIT_CONFIG } from 'state/constants';
import { format } from 'date-fns';


const toDateStr = (val) => {
    if (!val) return '';
    try { return format(new Date(val), 'yyyy-MM-dd'); } catch { return ''; }
};

const fromStorage = (cfg) => ({
    validSince: toDateStr(cfg.validSince),   // normalize Date obj / ISO string → 'yyyy-MM-dd'
    validUntil: toDateStr(cfg.validUntil),   // '' when null (controlled input requires string)
    personalDeduction:  cfg.personalDeduction  ?? DEFAULT_PIT_CONFIG.personalDeduction,
    dependentDeduction: cfg.dependentDeduction ?? DEFAULT_PIT_CONFIG.dependentDeduction,
    brackets: (cfg.brackets ?? []).map(b => ({
        max:       b.max === null ? null : b.max,
        rate:      +(b.rate * 100).toFixed(4),
        deduction: b.deduction,
    })),
    rules: (cfg.rules ?? []).map(r => ({
        limit:    r.limit,
        subtract: r.subtract,
        rate:     +(r.rate * 100).toFixed(4),
    })),
});

// Chuyển về storage format trước khi gửi API
const toStorage = (cfg) => ({
    validSince: cfg.validSince || null,
    validUntil: cfg.validUntil || null,
    personalDeduction:  Number(cfg.personalDeduction)  || 0,
    dependentDeduction: Number(cfg.dependentDeduction) || 0,
    brackets: cfg.brackets.map(b => ({
        max:       b.max === null || b.max === '' ? null : Number(b.max),
        rate:      Number(b.rate) / 100,
        deduction: Number(b.deduction),
    })),
    rules: cfg.rules.map(r => ({
        limit:    Number(r.limit),
        subtract: Number(r.subtract),
        rate:     Number(r.rate) / 100,
    })),
});

const numField = (value, onChange, opts = {}) => (
    <TextField
        size="small"
        type="number"
        value={value ?? ''}
        onChange={e => onChange(e.target.value)}
        slotProps={{ input: { inputProps: { style: { textAlign: 'right', padding: '4px 6px' } } } }}
        sx={{ width: opts.width ?? 120 }}
        {...opts}
    />
);

const PitConfigDialog = ({
    open,
    onClose,
    initialConfig,
    onSaved
}) => {
    const init = initialConfig ?? DEFAULT_PIT_CONFIG;
    const [cfg, setCfg] = useState(() => fromStorage(init));

    /* ---------- Brackets ---------- */
    const setBracket = (i, field, val) =>
        setCfg(prev => ({
            ...prev,
            brackets: prev.brackets.map((b, j) => j === i ? { ...b, [field]: val } : b),
        }));

    const addBracket = () =>
        setCfg(prev => ({
            ...prev,
            brackets: [
                ...prev.brackets.slice(0, -1),
                { max: 0, rate: 0, deduction: 0 },
                prev.brackets[prev.brackets.length - 1],
            ],
        }));

    const removeBracket = (i) =>
        setCfg(prev => ({ ...prev, brackets: prev.brackets.filter((_, j) => j !== i) }));

    /* ---------- Rules ---------- */
    const setRule = (i, field, val) =>
        setCfg(prev => ({
            ...prev,
            rules: prev.rules.map((r, j) => j === i ? { ...r, [field]: val } : r),
        }));

    const addRule = () =>
        setCfg(prev => ({
            ...prev,
            rules: [...prev.rules, { limit: 0, subtract: 0, rate: 0 }],
        }));

    const removeRule = (i) =>
        setCfg(prev => ({ ...prev, rules: prev.rules.filter((_, j) => j !== i) }));

    /* ---------- Save ---------- */
    const handleSave = () => {
        if (!cfg.validSince) {
            toast.error('Cần nhập ngày hiệu lực (Có hiệu lực từ).');
            return;
        }
        const stored = toStorage(cfg);
        if (stored.brackets.length === 0) {
            toast.error('Cần ít nhất 1 bậc thuế.');
            return;
        }
        onSaved(stored);
    };

    const handleReset = () => setCfg(fromStorage(DEFAULT_PIT_CONFIG));

    const chipLabel = cfg.validSince
        ? cfg.validUntil ? `${cfg.validSince} → ${cfg.validUntil}` : `Từ ${cfg.validSince}`
        : 'Chưa đặt ngày';  // validUntil is '' (not null) here so falsy check is safe

    return (
        <Dialog open={open} onClose={onClose} maxWidth="lg" fullWidth>
            <DialogTitle>
                <FlexBetween>
                    <Box display="flex" alignItems="center" gap={1.5}>
                        <Typography fontWeight="bold">Cấu hình thuế TNCN</Typography>
                        <Chip label={chipLabel} size="small" color="primary" variant="outlined" />
                    </Box>
                    <IconButton size="small" onClick={onClose}>
                        <Close fontSize="small" />
                    </IconButton>
                </FlexBetween>
            </DialogTitle>

            <DialogContent dividers>
                {/* ── VALIDITY DATES ── */}
                <Box display="flex" gap={3} mb={2} flexWrap="wrap">
                    <Box>
                        <Typography variant="caption" color="text.secondary" display="block" mb={0.5}>
                            Có hiệu lực từ *
                        </Typography>
                        <TextField
                            size="small"
                            type="date"
                            value={cfg.validSince}
                            onChange={e => setCfg(prev => ({ ...prev, validSince: e.target.value }))}
                            sx={{ width: 180 }}
                        />
                    </Box>
                    <Box>
                        <Typography variant="caption" color="text.secondary" display="block" mb={0.5}>
                            Đến ngày (để trống = không giới hạn)
                        </Typography>
                        <TextField
                            size="small"
                            type="date"
                            value={cfg.validUntil}
                            onChange={e => setCfg(prev => ({ ...prev, validUntil: e.target.value }))}
                            sx={{ width: 180 }}
                        />
                    </Box>
                </Box>

                <Divider sx={{ my: 2 }} />

                {/* ── DEDUCTIONS ── */}
                <Typography variant="subtitle1" fontWeight="bold" mb={1}>
                    Giảm trừ gia cảnh
                </Typography>
                <Box display="flex" gap={3} mb={1} flexWrap="wrap">
                    <Box>
                        <Typography variant="caption" color="text.secondary" display="block" mb={0.5}>
                            Giảm trừ bản thân (VNĐ/tháng)
                        </Typography>
                        {numField(
                            cfg.personalDeduction,
                            v => setCfg(prev => ({ ...prev, personalDeduction: v })),
                            { width: 200 }
                        )}
                    </Box>
                    <Box>
                        <Typography variant="caption" color="text.secondary" display="block" mb={0.5}>
                            Giảm trừ mỗi người phụ thuộc (VNĐ/tháng)
                        </Typography>
                        {numField(
                            cfg.dependentDeduction,
                            v => setCfg(prev => ({ ...prev, dependentDeduction: v })),
                            { width: 200 }
                        )}
                    </Box>
                </Box>

                <Divider sx={{ my: 2 }} />

                {/* ── BRACKETS ── */}
                <Box display="flex" alignItems="center" justifyContent="space-between" mb={1}>
                    <Typography variant="subtitle1" fontWeight="bold">
                        Biểu thuế lũy tiến (Gross → Tax)
                    </Typography>
                    <Button size="small" startIcon={<Add />} onClick={addBracket}>
                        Thêm bậc
                    </Button>
                </Box>
                <Typography variant="caption" color="text.secondary" mb={1} display="block">
                    Bậc cuối để max trống = không giới hạn (∞). Rate nhập theo %, ví dụ: 5 = 5%.
                </Typography>

                <TableContainer sx={{ mb: 3, border: '1px solid', borderColor: 'divider', borderRadius: 1 }}>
                    <Table size="small" stickyHeader>
                        <TableHead>
                            <TableRow>
                                <TableCell sx={{ fontWeight: 'bold', width: 40 }}>#</TableCell>
                                <TableCell sx={{ fontWeight: 'bold' }}>Thu nhập tối đa (max)</TableCell>
                                <TableCell sx={{ fontWeight: 'bold' }}>Thuế suất (rate %)</TableCell>
                                <TableCell sx={{ fontWeight: 'bold' }}>Số giảm trừ (deduction)</TableCell>
                                <TableCell sx={{ width: 48 }} />
                            </TableRow>
                        </TableHead>
                        <TableBody>
                            {cfg.brackets.map((b, i) => (
                                <TableRow key={i} sx={{ '&:hover': { bgcolor: 'action.hover' } }}>
                                    <TableCell sx={{ color: 'text.secondary', fontSize: '0.75rem' }}>
                                        {i + 1}
                                    </TableCell>
                                    <TableCell>
                                        {i === cfg.brackets.length - 1 ? (
                                            <Chip label="∞ (không giới hạn)" size="small" variant="outlined" />
                                        ) : (
                                            numField(b.max, v => setBracket(i, 'max', v === '' ? null : v), { width: 160 })
                                        )}
                                    </TableCell>
                                    <TableCell>
                                        {numField(b.rate, v => setBracket(i, 'rate', v), { width: 90 })}
                                    </TableCell>
                                    <TableCell>
                                        {numField(b.deduction, v => setBracket(i, 'deduction', v), { width: 160 })}
                                    </TableCell>
                                    <TableCell>
                                        <IconButton
                                            size="small"
                                            color="error"
                                            onClick={() => removeBracket(i)}
                                            disabled={cfg.brackets.length <= 1}
                                        >
                                            <Delete fontSize="small" />
                                        </IconButton>
                                    </TableCell>
                                </TableRow>
                            ))}
                        </TableBody>
                    </Table>
                </TableContainer>

                <Divider sx={{ my: 2 }} />

                {/* ── RULES ── */}
                <Box display="flex" alignItems="center" justifyContent="space-between" mb={1}>
                    <Typography variant="subtitle1" fontWeight="bold">
                        Quy tắc quy đổi Net → Gross
                    </Typography>
                    <Button size="small" startIcon={<Add />} onClick={addRule}>
                        Thêm quy tắc
                    </Button>
                </Box>
                <Typography variant="caption" color="text.secondary" mb={1} display="block">
                    Sắp xếp theo limit giảm dần. Rate nhập theo %, ví dụ: 65 = 65%.
                </Typography>

                <TableContainer sx={{ border: '1px solid', borderColor: 'divider', borderRadius: 1 }}>
                    <Table size="small" stickyHeader>
                        <TableHead>
                            <TableRow>
                                <TableCell sx={{ fontWeight: 'bold', width: 40 }}>#</TableCell>
                                <TableCell sx={{ fontWeight: 'bold' }}>Ngưỡng thu nhập (limit)</TableCell>
                                <TableCell sx={{ fontWeight: 'bold' }}>Số trừ (subtract)</TableCell>
                                <TableCell sx={{ fontWeight: 'bold' }}>Hệ số (rate %)</TableCell>
                                <TableCell sx={{ width: 48 }} />
                            </TableRow>
                        </TableHead>
                        <TableBody>
                            {cfg.rules.map((r, i) => (
                                <TableRow key={i} sx={{ '&:hover': { bgcolor: 'action.hover' } }}>
                                    <TableCell sx={{ color: 'text.secondary', fontSize: '0.75rem' }}>
                                        {i + 1}
                                    </TableCell>
                                    <TableCell>
                                        {numField(r.limit, v => setRule(i, 'limit', v), { width: 160 })}
                                    </TableCell>
                                    <TableCell>
                                        {numField(r.subtract, v => setRule(i, 'subtract', v), { width: 160 })}
                                    </TableCell>
                                    <TableCell>
                                        {numField(r.rate, v => setRule(i, 'rate', v), { width: 90 })}
                                    </TableCell>
                                    <TableCell>
                                        <IconButton
                                            size="small"
                                            color="error"
                                            onClick={() => removeRule(i)}
                                            disabled={cfg.rules.length <= 1}
                                        >
                                            <Delete fontSize="small" />
                                        </IconButton>
                                    </TableCell>
                                </TableRow>
                            ))}
                        </TableBody>
                    </Table>
                </TableContainer>
            </DialogContent>

            <DialogActions sx={{ px: 3, py: 1.5, justifyContent: 'space-between' }}>
                <Button size="small" color="warning" startIcon={<RestartAlt />} onClick={handleReset}>
                    Reset to default
                </Button>
                <Box display="flex" gap={1}>
                    <Button variant="outlined" size="small" onClick={handleSave}>
                        Save
                    </Button>
                    <Button variant="outlined" size="small" color="secondary" onClick={onClose}>
                        Cancel
                    </Button>
                </Box>
            </DialogActions>
        </Dialog>
    );
};

export default PitConfigDialog;
